import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CustomValidators, Position } from 'cloud-shared-lib';
import * as _ from 'lodash';
import { Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CuttingCustomHoleSchema } from '../../../../models/cutting/cutting-custom-hole-schema.model';
import { FastenerBasePointEnum } from '../../../../models/cutting/cutting-custom-hole.model';
import { CuttingFastenersSet } from '../../../../models/cutting/cutting-fasteners-set.model';
import { PlateTypeEnum } from '../../../../models/cutting/cutting-panel-outline.model';
import { FastenerSessionStorageManager } from '../../fastener-selection/fastener-session-storage-manager.helper';
import { CustomHoleCoordinates, CustomHoleParansForm } from '../fastener-base-point-select.component';
import { PlateHoleCoordinatesFormComponent } from './plate-hole-coord/plate-hole-coordinates-form.component';

@Component({
  selector: 'csl-plate-hole-form',
  templateUrl: './plate-hole-form.component.html',
  styleUrls: ['./plate-hole-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PlateHoleFormComponent implements OnInit, OnDestroy {
  holeForm: UntypedFormGroup;
  holeFormErrors: any;
  thisFastenerPlateTypeEnum = PlateTypeEnum;
  unsubscribe$: Subject<void> = new Subject();
  depthMaxValue = 0;

  @Input() availablePlate: PlateTypeEnum;
  @Input() fastener: CuttingCustomHoleSchema | CuttingFastenersSet;
  @Input() data: CustomHoleParansForm;
  @Input() panelHeight: number;
  @Input() panelWidth: number;
  @Input() side: Position | undefined;
  @Input() fullMaterialThickness = 0;
  @Input() selectedMultiSide: Position;
  @Input() point: FastenerBasePointEnum;

  @Output() formChanged: EventEmitter<CustomHoleParansForm> = new EventEmitter();

  get isThrough(): boolean {
    return !this.isFastenersSet(this.fastener) && this.fastener.maxDepth === 0 && this.fastener.minDepth === 0;
  }

  @ViewChild('coordForm', { static: true }) coordForm: PlateHoleCoordinatesFormComponent;

  constructor(private formBuilder: UntypedFormBuilder) {
    this.holeFormErrors = {
      type: {},
      depth: {},
    };
  }

  ngOnInit(): void {
    const hasAvailablePlate = !_.isNil(this.availablePlate);

    this.holeForm = this.formBuilder.group({
      type: [
        {
          value: hasAvailablePlate ? this.availablePlate : this.data.type,
          disabled:
            this.isThrough ||
            hasAvailablePlate ||
            (this.isFastenersSet(this.fastener) && this.fastener.forbidChangeSide),
        },
        [Validators.required],
      ],
      depth: [this.data.depth, []],
    });

    if (!this.isFastenersSet(this.fastener)) {
      if (this.fastener.maxDepth === this.fastener.minDepth) {
        this.data.depth = this.fastener.minDepth;
        this.depthMaxValue = this.fullMaterialThickness;
        this.holeForm.controls.depth.disable();
        this.holeForm.controls.depth.setValidators([
          Validators.required,
          CustomValidators.number,
          Validators.min(this.fastener.minDepth),
          Validators.max(this.fastener.minDepth),
        ]);
      } else {
        this.depthMaxValue = Math.min(this.fastener.maxDepth, this.fullMaterialThickness);

        this.holeForm.controls.depth.setValidators([
          Validators.required,
          CustomValidators.number,
          Validators.min(this.fastener.minDepth),
          Validators.max(this.depthMaxValue),
        ]);
      }
    }

    this.holeForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.onHoleFormChanged();

      if (this.holeForm.valid) {
        this.data.type = this.holeForm.controls.type.value;
        this.data.depth = this.holeForm.controls.depth.value;

        this.formChanged.emit(this.data.coords === undefined ? undefined : this.data);
      } else {
        this.formChanged.emit(undefined);
      }
    });

    if (!this.isFastenersSet(this.fastener)) {
      // only for new holes
      this.holeForm.controls.depth.setValue(this.fastener.minDepth);
    }
  }

  holesCoordinatesFormChanged(coords: CustomHoleCoordinates[] | undefined): void {
    if (coords !== undefined) {
      this.data.coords = coords;
      this.formChanged.emit(this.holeForm.invalid ? undefined : this.data);
    } else {
      this.formChanged.emit(undefined);
    }
  }

  onHoleFormChanged(): void {
    for (const field in this.holeFormErrors) {
      if (!this.holeFormErrors.hasOwnProperty(field)) {
        continue;
      }

      // Clear previous errors
      this.holeFormErrors[field] = {};

      // Get the control
      const control = this.holeForm.get(field);

      if (control && control.dirty && !control.valid) {
        this.holeFormErrors[field] = control.errors;
      }
    }
  }

  getError(errors: any): Observable<string> {
    if (!errors) {
      return of('');
    }

    if (errors.min) {
      return of($localize`Минимальное значение - ${errors.min.min}`);
    }

    if (errors.max) {
      return of($localize`Максимальное значение - ${errors.max.max}`);
    }

    if (errors.required) {
      return of($localize`Поле не должно быть пустым`);
    }

    if (errors.number) {
      return of($localize`Необходимо ввести число`);
    }
  }

  isFastenersSet(item: CuttingCustomHoleSchema | CuttingFastenersSet): item is CuttingFastenersSet {
    return item instanceof CuttingFastenersSet;
  }

  setPlateTypeToSS(val: PlateTypeEnum): void {
    FastenerSessionStorageManager.lastSelectedPlate = val;
  }

  addCoord(): void {
    this.coordForm.addCoordAndFocusLast();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }
}
